<template>
  <vs-wrapper space="small">
    <vs-dialog
      v-if="isDeleteDialogVisible"
      visible
      title="Delete Attachment"
      data-test="delete-dialog"
      @on-close="closeDialog"
    >
      <vs-wrapper>
        <vs-text
          >You are deleting {{ currentlySelectedAttachment?.filename }}. This action can not be
          undone.
        </vs-text>
        <v-row justify="end">
          <v-col class="shrink pr-2">
            <vs-button label="Cancel" type="secondary" @click="closeDialog()" />
          </v-col>
          <v-col class="shrink">
            <vs-button
              data-test="attachment-delete-confirmation"
              label="Delete"
              type="primary"
              destructive
              @click="deleteAttachmentMethod"
            />
          </v-col>
        </v-row>
      </vs-wrapper>
    </vs-dialog>

    <vs-heading type="overline">Attachments</vs-heading>

    <attachment-card
      v-for="attachment in attachments"
      :key="attachment.filename"
      :secondary-text="hasMetadata(attachment) ? getUploadInfo(attachment) : null"
      :can-delete="enableDelete && hasMetadata(attachment) && canDelete(attachment)"
      @delete-attachment="openDialog(attachment)"
    >
      <image-attachment
        v-if="isImage(attachment.filename) && getDownloadUrl(attachment)"
        :filename="attachment.filename"
        :url="getDownloadUrl(attachment)"
      />

      <template #primary-text>
        <a
          :href="getDownloadUrl(attachment)"
          target="_blank"
          :class="isLoading && isAttachmentLoading(attachment) ? 'loading' : null"
          @click="(event) => handleAttachmentClick(attachment, event)"
          >{{ attachment.filename }}</a
        >
      </template>
    </attachment-card>
    <slot></slot>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import moment from 'moment/moment'
import JobAttachment from '@/models/job_attachment'
import { Attachment } from '@/models/attachment'
import User from '@/models/user'
import { AcceptedImageExtensions } from '@/utils/constants'
import { getInstance } from '@/auth'
import type { User as UserAuth0 } from '@auth0/auth0-spa-js/dist/typings/global'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsDialog from '@/components/vision/VsDialog.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import AttachmentCard from '@/components/tickets/AttachmentCard.vue'
import ImageAttachment from '@/components/common/ImageAttachment.vue'
import VsText from '@/components/vision/VsText.vue'

type AttachmentType = JobAttachment | Attachment

const props = withDefaults(
  defineProps<{
    jobUuid?: string | null
    clientOnly: boolean
    jobAttachments: JobAttachment[]
    attachmentStorageId?: string | null
    enableDelete: boolean
  }>(),
  {
    jobUuid: null,
    attachmentStorageId: '',
  },
)

const emit = defineEmits(['attachment-removed'])

onMounted(() => {
  if (props.attachmentStorageId) fetchAttachments(props.attachmentStorageId)
})

const auth: { user: UserAuth0 } = getInstance()

const isDeleteDialogVisible = ref(false)

const currentlySelectedAttachment = ref<AttachmentType | null>(null)

const loadingAttachments = ref<string[]>([])

const store = useStore()

const getAttachments = computed<(storageId: string) => Attachment[]>(
  () => store.getters['attachments/getAllAttachmentsForStorageId'],
)
const getUserBySid = computed<(userSid: string) => User>(() => store.getters['users/getBySid'])

const canDeleteAttachments = computed<boolean>(
  () => store.getters['permissions/canDeleteAttachments'],
)

const isLoading = computed(() => loadingAttachments.value.length > 0)

const fetchAttachments = async (attachmentStorageId: string | null) =>
  await store.dispatch('attachments/fetchAttachments', attachmentStorageId)

const deleteAttachment = async (arg: { filename: string; storageId: string }) =>
  await store.dispatch('attachments/deleteAttachment', arg)

const removeJobAttachment = async (arg: { jobId: string; attachmentId: string }) =>
  await store.dispatch('jobs/removeAttachment', arg)

const firebaseAttachments = computed(() =>
  props.attachmentStorageId ? getAttachments.value(props.attachmentStorageId) : [],
)
const attachments = computed(() => [
  ...firebaseAttachments.value,
  ...[...props.jobAttachments].sort((a, b) => a.created.diff(b.created)),
])

function isJobAttachment(attachment: AttachmentType | null) {
  return attachment instanceof JobAttachment
}

function getUploadInfo(attachment: AttachmentType) {
  const user = getUserBySid.value(
    (isJobAttachment(attachment)
      ? (attachment as JobAttachment).createdBy
      : (attachment as Attachment).metadata?.createdBy) ?? '',
  )
  const userName = user ? user.name : null

  const uploadedTime = moment(
    isJobAttachment(attachment)
      ? (attachment as JobAttachment).created.local()
      : (attachment as Attachment).metadata?.timestamp,
  ).format('MMM DD, HH:mm')

  return userName ? `${userName} on ${uploadedTime}` : `${uploadedTime}`
}

function canDelete(attachment: AttachmentType) {
  const isUploader =
    (isJobAttachment(attachment)
      ? (attachment as JobAttachment).createdBy
      : (attachment as Attachment).metadata?.createdBy) === auth.user.sub
  return canDeleteAttachments.value || isUploader
}

function deleteAttachmentMethod() {
  if (isJobAttachment(currentlySelectedAttachment.value)) {
    emit('attachment-removed', (currentlySelectedAttachment.value as JobAttachment).id)

    if (!props.clientOnly) {
      removeJobAttachment({
        jobId: props.jobUuid ?? '',
        attachmentId: (currentlySelectedAttachment.value as JobAttachment).id.toString(),
      })
    }
  } else {
    deleteAttachment({
      filename: (currentlySelectedAttachment.value as Attachment).filename,
      storageId: props.attachmentStorageId ?? '',
    })
  }
  closeDialog()
}

function isImage(filename: string) {
  const extension = filename.split('.').pop()

  return AcceptedImageExtensions.includes(extension?.toLowerCase() ?? '')
}

function openDialog(attachment: AttachmentType | null) {
  currentlySelectedAttachment.value = attachment
  isDeleteDialogVisible.value = true
}

function closeDialog() {
  currentlySelectedAttachment.value = null
  isDeleteDialogVisible.value = false
}

function getDownloadUrl(attachment: AttachmentType) {
  return isJobAttachment(attachment)
    ? (attachment as JobAttachment).source
    : (attachment as Attachment).downloadUrl
}

function hasMetadata(attachment: AttachmentType) {
  return isJobAttachment(attachment) || !!(attachment as Attachment).metadata
}

/**
 * Check if we need to refresh Azure Storage attachment links before letting route
 */
async function handleAttachmentClick(attachment: AttachmentType, event: Event) {
  // We only check jobAttachments for link expiration
  if (!isJobAttachment(attachment) || isTokenValid(attachment as JobAttachment)) return

  // Prevent link from opening since token is expired
  event.preventDefault()

  await refreshAttachment(attachment as JobAttachment)
  window.open((attachment as JobAttachment).source)
}

function isTokenValid(attachment: JobAttachment) {
  const url = attachment.source
  const urlParams = new URLSearchParams(url)
  const expiry = urlParams.get('se')

  if (expiry) {
    const date = Date.parse(expiry)
    const now = Date.parse(new Date().toISOString())
    return date > now
  }

  // Could not get expiry from the url. Assume still valid to prevent any infinite loops
  return true
}

async function refreshAttachment(attachment: JobAttachment) {
  markAttachmentLoading(attachment.id)

  const refreshedAttachment = await store.dispatch('jobs/fetchAttachment', {
    jobId: props.jobUuid,
    attachmentId: attachment.id,
  })

  if (refreshedAttachment) {
    // Job side-sheet is not reactive to store updates so we force it here by updating the source property
    attachment.source = refreshedAttachment.source
  }

  markAttachmentLoaded(attachment.id)
}

function markAttachmentLoading(attachmentId: string) {
  if (loadingAttachments.value.indexOf(attachmentId) !== -1) return

  loadingAttachments.value.push(attachmentId)
}

function markAttachmentLoaded(attachmentId: string) {
  const index = loadingAttachments.value.indexOf(attachmentId)
  if (index === -1) return

  loadingAttachments.value.splice(index, 1)
}

function isAttachmentLoading(attachment: AttachmentType): boolean {
  if (!isJobAttachment(attachment)) return false

  return loadingAttachments.value.includes((attachment as JobAttachment).id)
}
</script>

<style scoped>
.attachment-content {
  margin-top: 7px;
  font-size: 18px;
}

.loading {
  pointer-events: none;
  cursor: default;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 20%) 20%,
    rgba(0, 0, 0, 40%) 30%,
    rgba(0, 0, 0, 60%) 70%,
    rgba(0, 0, 0, 80%) 80%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
  background-size: 200% auto;
  animation: textShine 7s ease-in-out infinite alternate;
}

@keyframes textShine {
  to {
    background-position: 200%;
  }
}
</style>
